/**
 * The design's filled-segment control (OO5): a `#F4F4F5` well with 5px corners and 3px of
 * padding, the segments 4px, the active one white on a 1px 2px lift. Straight from the artboard's
 * top-right screen switcher (`docs/planning/design/apex-analyzer-redesign.dc.html`), which is the
 * only segmented control the design draws. Used for Escrow / Pipeline / List and By deal /
 * Agenda / Calendar - a VIEW switch, never a form field.
 */
interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label'?: string;
  className?: string;
}

export function Segmented<T extends string>({ options, value, onChange, className = '', ...props }: SegmentedProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={props['aria-label']}
      className={`inline-flex items-center gap-0.5 rounded-[5px] bg-[#F4F4F5] p-[3px] ${className}`}
    >
      {options.map(o => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={`whitespace-nowrap rounded-lg px-3 py-[7px] text-[12px] font-semibold leading-none transition-colors ${
              active ? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(17,17,17,.08)]' : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
